import React from 'react';
import axios from 'axios';
import { OrderedMap } from 'immutable';
import { useTranslation } from 'react-i18next';
import {
  Button, Checkbox, Form, Header, Icon, Image, Input, List, Loader, Message, Modal, Segment,
} from 'semantic-ui-react';
import Upload from '../helpers/components/Upload';
import QuantityField from '../helpers/components/QuantityField';
import QualityLevel from './QualityLevel';

const buildUrl = (query, prefixes) => {
  const params = [`search=${encodeURIComponent(query)}`];
  prefixes.forEach((prefix) => params.push(`prefix=${encodeURIComponent(prefix)}`));
  return `/api/v1/products/?${params.join('&')}`;
};

const PcItemModal = ({ component, quotationId, open, onClose, onItemAdded }) => {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [qualityLevels, setQualityLevels] = React.useState(null);
  const [qualityLevel, setQualityLevel] = React.useState(null);
  const [quantity, setQuantity] = React.useState(1);
  const [comment, setComment] = React.useState('');
  const [isSealed, setIsSealed] = React.useState(false);
  const [custom, setCustom] = React.useState(false);
  const [customName, setCustomName] = React.useState(component ? component.prefixes[0] || '' : '');
  const [files, setFiles] = React.useState(OrderedMap());
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const componentNames = {
    processor: t('Processor'),
    cooler: t('Cooler'),
    motherboard: t('Motherboard'),
    memory: t('RAM memory'),
    storage: t('Storage'),
    case: t('PC case'),
    power: t('Power supply'),
    video: t('Video card'),
    extras: t('Peripherals, accessories and extras'),
  };

  React.useEffect(() => {
    if (!open || !component) return undefined;
    setQuery('');
    setResults([]);
    setSelectedProduct(null);
    setQualityLevels(null);
    setQualityLevel(null);
    setQuantity(1);
    setComment('');
    setIsSealed(false);
    setCustom(false);
    setCustomName(component.prefixes[0] || '');
    setFiles(OrderedMap());
    setError('');
    return undefined;
  }, [open, component]);

  React.useEffect(() => {
    if (!open || !component) return undefined;
    const timeout = setTimeout(() => {
      setLoading(true);
      axios.get(buildUrl(query, component.prefixes))
        .then(({ data }) => setResults(data.results || []))
        .catch(() => setError(t('Could not search products right now.')))
        .finally(() => setLoading(false));
    }, query ? 300 : 0);
    return () => clearTimeout(timeout);
  }, [open, component, query]);

  React.useEffect(() => {
    if (!selectedProduct || !selectedProduct.id) {
      if (selectedProduct) {
        setError(t('Could not identify the selected product. Choose it again.'));
        setSelectedProduct(null);
      }
      return undefined;
    }
    setQualityLevels(null);
    axios.get(`/api/v1/quality-levels/?product=${selectedProduct.id}`)
      .then(({ data }) => setQualityLevels(data))
      .catch(() => setError(t('Could not load this item conditions.')));
    return undefined;
  }, [selectedProduct]);

  const close = () => {
    files.forEach((file) => URL.revokeObjectURL(file.preview));
    onClose();
  };

  const saveItem = () => {
    const imageIds = files.valueSeq().toArray().filter((file) => file.uploaded).map((file) => file.id);
    const payload = {
      quotation: parseInt(quotationId, 10),
      quantity,
      comment,
      images: imageIds,
    };

    if (custom) {
      payload.custom_product = customName.trim();
      payload.is_sealed = isSealed;
    } else {
      payload.product = selectedProduct.id;
      payload.quality_level = qualityLevel && qualityLevel.id;
    }

    if ((custom && (!payload.custom_product || payload.custom_product === customPrefix.trim())) || (!custom && !payload.quality_level)) {
      setError(custom ? t('Enter the item name.') : t('Select the item condition.'));
      return;
    }

    setSaving(true);
    setError('');
    axios.post('/api/v1/quotation-items/', payload)
      .then(() => {
        onItemAdded();
        close();
      })
      .catch(() => setError(t('Could not add the item. Try again.')))
      .finally(() => setSaving(false));
  };

  const productDetails = selectedProduct || custom;
  const customPrefix = component && component.prefixes.length ? `${component.prefixes[0]} ` : '';

  return (
    <Modal open={open} onClose={close} closeIcon size="small" className="pc-item-modal">
      <Modal.Content className="pc-item-modal-content">
        <Header as="h2" className="pc-item-modal-title">{t('Add {{component}}', { component: component && componentNames[component.key] })}</Header>
        {error && <Message negative content={error} />}
        {!productDetails && (
          <>
            <Form.Field>
              <label htmlFor="pc-product-search">{t('Search for the item')}</label>
              <Input
                id="pc-product-search"
                fluid
                icon="search"
                placeholder={component && component.prefixes.length ? t('E.g. {{prefix}}', { prefix: component.prefixes[0] }) : t('Type the item name')}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </Form.Field>
            <Segment basic loading={loading} className="pc-search-results">
              <List selection divided>
              {!loading && results.map((product) => (
                <List.Item key={product.id} onClick={() => setSelectedProduct(product)}>
                  {product.cover_image && <Image avatar src={product.cover_image} />}
                  <List.Content><List.Header>{product.name}</List.Header></List.Content>
                </List.Item>
              ))}
              </List>
              {!loading && results.length === 0 && <p>{t('No products found.')}</p>}
            </Segment>
            <div className="pc-custom-entry">
              <Icon name="edit outline" />
              <div className="pc-custom-entry-copy">
                <strong>{t('Did not find the item you want to add?')}</strong>
                <span>{t('Add it manually. On the next step, you can describe it and send a photo.')}</span>
              </div>
              <Button primary size="small" className="pc-custom-entry-action" onClick={() => { setCustom(true); setCustomName(`${customPrefix}${query}`.trim()); }}>
                {t('Add item manually')}
              </Button>
            </div>
          </>
        )}
        {productDetails && (
          <Form onSubmit={saveItem} loading={saving}>
            {!custom && (
              <>
                <div className="pc-selected-product">
                  <div className="pc-selected-product-image">
                    {selectedProduct.cover_image
                      ? <Image src={selectedProduct.cover_image} />
                      : <Icon name="image outline" />}
                  </div>
                  <div>
                    <span>{t('Selected product')}</span>
                    <Header as="h3">{selectedProduct.name}</Header>
                  </div>
                </div>
                <div className="pc-condition-section">
                  {qualityLevels === null ? <Loader active inline="centered" /> : (
                    <QualityLevel
                      items={qualityLevels}
                      setSelectedProduct={(setter) => {
                        const next = setter({ quality_level: qualityLevel });
                        setQualityLevel(next.quality_level);
                      }}
                    />
                  )}
                </div>
              </>
            )}
            {custom && (
              <>
                <Form.Input
                  label={t('Item name')}
                  value={customName}
                  onChange={(event) => setCustomName(event.target.value)}
                  placeholder={t('Describe the item')}
                />
                <Form.Field>
                  <Checkbox
                    toggle
                    label={t('New item, with sealed package')}
                    checked={isSealed}
                    onChange={() => setIsSealed(!isSealed)}
                  />
                </Form.Field>
              </>
            )}
            <div className="pc-item-information">
              <Form.Field>
                <label htmlFor="pc-item-quantity">{t('Quantity')}</label>
                <QuantityField id="pc-item-quantity" value={quantity} onChange={setQuantity} />
              </Form.Field>
              <Form.TextArea label={t('Comment (optional)')} value={comment} onChange={(event) => setComment(event.target.value)} />
              <Upload label={t('Add a photo of your item (optional)')} quotation={quotationId} files={files} setFiles={setFiles} />
            </div>
            <div className="pc-item-modal-actions">
              <Button type="button" basic className="pc-modal-back" onClick={() => { setSelectedProduct(null); setCustom(false); }}>{t('Back to search')}</Button>
              <Button type="submit" primary className="pc-modal-submit" disabled={saving}>{t('Add item')}</Button>
            </div>
          </Form>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default PcItemModal;
